import React, { useEffect, useState } from 'react';
import { RadioChangeEvent } from 'antd';

import './index.less';

import IbButton from '../../../../../components/common/IbButton';
import IbModal from '../../../../../components/common/IbModal';
import { ProviderModel, ToolsetModel, ToolsetSettingsType } from '../../../../../../gamma-api';
import IbIcon from '../../../../../components/common/IbIcon';
import IbRadioGroup from '../../../../../components/common/IbRadioGroup';
import IbRadioButton from '../../../../../components/common/IbRadioButton';
import { getToolsetTypeIconName, getToolsetTypeName } from '../utils';
import IbTypography from '../../../../../components/common/IbTypography';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import {
  MAIN_CLASS_NAME,
  MODAL_MEDIUM_CLASS_NAME,
  MODAL_NARROW_CLASS_NAME,
  MODAL_WIDE_CLASS_NAME,
  MODAL_WIDTH_DEFAULT,
  RADIO_BUTTON_CLASS_NAME,
  RADIO_BUTTON_LABEL_CLASS_NAME,
  RADIO_BUTTON_LABEL_TITLE_CLASS_NAME,
  RADIO_GROUP_CLASS_NAME,
  RADIO_GROUP_COLUMN_CLASS_NAME,
} from './const';
import MCPServerParameters from './MCPServerParameters';
import SmartSearchParameters from './SmartSearchParameters';
import WebParameters from './WebParameters';
import FileToolsParameters from './FileToolsParameters';
import AgentParameters from './AgentParameters';
import Elma365GeneralParameters from './Elma365GeneralParameters';
import Elma365ToolkitParameters from './Elma365ToolkitParameters';

export enum ToolsetModalStep {
  ToolsetTypeSelection = 'ToolsetTypeSelection',
  Parameters = 'Parameters',
}

export interface IToolsetModalProps {
  visible: boolean;
  toolset?: ToolsetModel;
  providers: ProviderModel[];
  providersLoading: boolean;
  onSave?: (toolset?: ToolsetModel) => Promise<void>;
  onClose?: () => void;
  onTypeChange?: (type: ToolsetSettingsType) => void;
}

const ToolsetModal: React.FC<IToolsetModalProps> = ({
  visible,
  toolset,
  providers,
  providersLoading,
  onSave,
  onClose,
  onTypeChange,
}) => {
  const [step, setStep] = useState<ToolsetModalStep>(ToolsetModalStep.ToolsetTypeSelection);
  const [editToolset, setEditToolset] = useState<ToolsetModel | undefined>(toolset);
  const [saving, setSaving] = useState(false);
  const [validationState, setValidationState] = useState(true);
  const [allowValidationErrorDisplay, setAllowValidationErrorDisplay] = useState(false);

  const translate = getTranslateFunc('Agent: Toolset: Modal:');

  useEffect(() => {
    if (!visible) {
      return;
    }

    setEditToolset(toolset);
    setValidationState(true);
    setAllowValidationErrorDisplay(false);
    setStep(toolset?.toolset_id ? ToolsetModalStep.Parameters : ToolsetModalStep.ToolsetTypeSelection);
  }, [visible, toolset?.type]);

  const onModalCancel = () => {
    onClose?.();
  };

  const onToolsetTypeChange = (e: RadioChangeEvent) => {
    onTypeChange?.(e.target.value);
  };

  const onParametersChange = (updatedToolset: ToolsetModel) => {
    setEditToolset(updatedToolset);
  };

  const onValidationStateChange = (state: boolean) => {
    setValidationState(state);
  };

  const renderTitle = () => {
    if (step === ToolsetModalStep.ToolsetTypeSelection) {
      return translate('Type selection title');
    }

    if (editToolset) {
      return getToolsetTypeName(editToolset.type);
    }

    return null;
  };

  const renderTypeSelectionContent = () => {
    const leftColumnItems = [
      {
        value: ToolsetSettingsType.McpServer,
        title: translate('MCP Server: Title'),
        description: translate('MCP Server: Description'),
      },
      {
        value: ToolsetSettingsType.SmartSearch,
        title: translate('Smart search: Title'),
        description: translate('Smart search: Description'),
      },
      {
        value: ToolsetSettingsType.Elma365General,
        title: translate('ELMA365: General: Title'),
        description: translate('ELMA365: General: Description'),
      },
      {
        value: ToolsetSettingsType.File,
        title: translate('File: Title'),
        description: translate('File: Description'),
      },
    ];

    const rightColumnItems = [
      {
        value: ToolsetSettingsType.DateTime,
        title: translate('DateTime: Title'),
        description: translate('DateTime: Description'),
      },
      {
        value: ToolsetSettingsType.Web,
        title: translate('Web: Title'),
        description: translate('Web: Description'),
      },
      {
        value: ToolsetSettingsType.Elma365Toolkit,
        title: translate('ELMA365: Toolkit: Title'),
        description: translate('ELMA365: Toolkit: Description'),
      },
      {
        value: ToolsetSettingsType.Agent,
        title: translate('Agent: Title'),
        description: translate('Agent: Description'),
      },
    ];

    const renderItem = (item: { value: ToolsetSettingsType; title: string; description: string }) => {
      return (
        <IbRadioButton key={item.value} showCircle className={RADIO_BUTTON_CLASS_NAME} value={item.value}>
          <div className={RADIO_BUTTON_LABEL_CLASS_NAME}>
            <div className={RADIO_BUTTON_LABEL_TITLE_CLASS_NAME}>
              <IbIcon iconName={getToolsetTypeIconName(item.value)} size={20} />
              <IbTypography.Paragraph strong>{item.title}</IbTypography.Paragraph>
            </div>
            <IbTypography.Paragraph disabled type="secondary">
              {item.description}
            </IbTypography.Paragraph>
          </div>
        </IbRadioButton>
      );
    };

    return (
      <>
        <IbRadioGroup
          buttonStyle="outline"
          className={RADIO_GROUP_CLASS_NAME}
          optionType="default"
          value={editToolset?.type}
          onChange={onToolsetTypeChange}
        >
          <div className={RADIO_GROUP_COLUMN_CLASS_NAME}>{leftColumnItems.map(renderItem)}</div>
          <div className={RADIO_GROUP_COLUMN_CLASS_NAME}>{rightColumnItems.map(renderItem)}</div>
        </IbRadioGroup>
      </>
    );
  };

  const renderParametersContent = () => {
    if (!editToolset) {
      return null;
    }

    switch (editToolset.type) {
      case ToolsetSettingsType.McpServer:
        return <MCPServerParameters editToolset={editToolset} onChange={onParametersChange} />;
      case ToolsetSettingsType.SmartSearch:
        return (
          <SmartSearchParameters
            editToolset={editToolset}
            onChange={onParametersChange}
            onValidationStateChange={onValidationStateChange}
          />
        );
      case ToolsetSettingsType.Web:
        return <WebParameters editToolset={editToolset} onChange={onParametersChange} />;
      case ToolsetSettingsType.Elma365General:
        return (
          <Elma365GeneralParameters
            editToolset={editToolset}
            providers={providers}
            providersLoading={providersLoading}
            onChange={onParametersChange}
          />
        );
      case ToolsetSettingsType.Elma365Toolkit:
        return (
          <Elma365ToolkitParameters
            editToolset={editToolset}
            onChange={onParametersChange}
            onValidationStateChange={onValidationStateChange}
          />
        );
      case ToolsetSettingsType.File:
        return (
          <FileToolsParameters
            editToolset={editToolset}
            providers={providers}
            providersLoading={providersLoading}
            onChange={onParametersChange}
          />
        );
      case ToolsetSettingsType.Agent:
        return (
          <AgentParameters
            allowValidationErrorDisplay={allowValidationErrorDisplay}
            editToolset={editToolset}
            onChange={onParametersChange}
            onValidationStateChange={onValidationStateChange}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (step === ToolsetModalStep.ToolsetTypeSelection) {
      return renderTypeSelectionContent();
    }

    if (step === ToolsetModalStep.Parameters) {
      return renderParametersContent();
    }

    return null;
  };

  const renderButtons = () => {
    const onNextClick = () => {
      setStep(ToolsetModalStep.Parameters);
    };

    const onSaveButtonClick = async () => {
      setAllowValidationErrorDisplay(true);
      if (!validationState) return;

      setSaving(true);
      await onSave?.(editToolset);
      setSaving(false);
    };

    if (editToolset?.type === ToolsetSettingsType.DateTime || step === ToolsetModalStep.Parameters) {
      return (
        <>
          <IbButton loading={saving} onClick={onSaveButtonClick}>
            {translate('Save')}
          </IbButton>
          <IbButton type="secondary" onClick={onModalCancel}>
            {translate('Cancel')}
          </IbButton>
        </>
      );
    }

    if (step == ToolsetModalStep.ToolsetTypeSelection) {
      return (
        <>
          <IbButton onClick={onNextClick}>{translate('Next')}</IbButton>
          <IbButton type="secondary" onClick={onModalCancel}>
            {translate('Cancel')}
          </IbButton>
        </>
      );
    }

    return null;
  };

  const classes = [MAIN_CLASS_NAME];
  if (step === ToolsetModalStep.Parameters) {
    if (editToolset?.type === ToolsetSettingsType.McpServer || editToolset?.type === ToolsetSettingsType.File) {
      classes.push(MODAL_MEDIUM_CLASS_NAME);
    } else if (
      editToolset?.type === ToolsetSettingsType.SmartSearch ||
      editToolset?.type === ToolsetSettingsType.Elma365General ||
      editToolset?.type === ToolsetSettingsType.Agent
    ) {
      classes.push(MODAL_WIDE_CLASS_NAME);
    } else {
      classes.push(MODAL_NARROW_CLASS_NAME);
    }
  }

  return (
    <IbModal
      className={classes.join(' ')}
      footer={renderButtons()}
      title={renderTitle()}
      visible={visible}
      width={MODAL_WIDTH_DEFAULT}
      onCancel={onModalCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default ToolsetModal;
